/**
 *
 * DigitalServicesUnavailable
 *
 * Inline message shown in place of the origin/destination form when digital
 * services are not offered for the current country/segment (e.g. private
 * customers in Sweden). Directs the user to ExpressEasy instead.
 *
 */

import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { DhlFlex, DhlText } from "@dhl-official/react-library";
import { Icon, Headline, Link } from "root/libs/ui-components/src";
import { breakpoints } from "root/libs/ui-styleguide/src";
import infoIcon from "iconsTwoColors/cx/info_filled.svg";
import externalLinkIcon from "icons/navigation/external_link.svg";
import styled from "styled-components";

const Card = styled.div`
  background: var(--dui-color-white-500);
  border: 1px solid var(--dui-color-gray-300);
  border-radius: var(--dui-size-radius-default, 4px);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--gaq-size-space-50px) var(--dui-size-space-8x);

  @media screen and (min-width: ${breakpoints.tablet}) {
    padding: var(--dui-size-space-24x) var(--dui-size-space-16x);
  }
`;

const StyledIcon = styled(Icon)`
  margin: 0 auto var(--dui-size-space-5x) auto;
`;

const Description = styled(DhlText)`
  display: block;
  margin: var(--dui-size-space-3x) auto var(--dui-size-space-8x) auto;

  & > *:is(p, span) {
    text-align: center;
  }
`;

const IconContainer = styled.span`
  position: relative;
  top: 1px;
  left: 8px;
`;
const StyledButtonContainer = styled(DhlFlex)`
  flex-direction: column;
  align-items: stretch;

  @media screen and (min-width: 48rem) {
    & {
      flex-direction: row;
      align-items: center;
    }
  }
`;

const COPY_ID_PREFIX = "DigitalServicesUnavailable";

const DigitalServicesUnavailable = ({ intl }) => {
  const getCopy = (id) => intl.formatMessage({ id: `${COPY_ID_PREFIX}.${id}` });

  const description = intl.formatMessage(
    { id: `${COPY_ID_PREFIX}.description` },
    {
      boldText: (
        <Link href={getCopy("linkUrl")} linkType="inline-black" target="_blank">
          {getCopy("descriptionBold")}
        </Link>
      ),
      boldText1: (
        <Link
          href={getCopy("linkUrl1")}
          linkType="inline-black"
          target="_blank"
        >
          {getCopy("descriptionBold1")}
        </Link>
      ),
    }
  );

  return (
    <div className="mx-wrapper">
      <div className="grid-maxWidthInner no-padding">
        <Card data-testid="digital-services-unavailable">
          <StyledIcon
            icon={infoIcon}
            useAs="decorative"
            width={{ mobile: "44px", desktop: "44px" }}
            height={{ mobile: "44px", desktop: "44px" }}
            extraStyles={[{ margin: "0 auto" }]}
          />
          <Headline priority={2} headlineDisplayType="h" weight="700">
            {getCopy("headline")}
          </Headline>
          <Description isParagraph>{description}</Description>
          <StyledButtonContainer
            justify="center"
            gap="var(--dui-size-space-4x)"
          >
            <Link
              href={getCopy("linkUrl1")}
              target="_blank"
              linkType="cta primary"
              dataTestid="go-to-mitt-cta"
            >
              {getCopy("linkText1")}
              <IconContainer>
                <Icon
                  fillColor="var(--dui-color-white-500)"
                  icon={externalLinkIcon}
                  useAs="decorative"
                  width={{ mobile: "14px", desktop: "14px" }}
                  height={{ mobile: "14px", desktop: "14px" }}
                />
              </IconContainer>
            </Link>
            <Link
              href={getCopy("linkUrl")}
              target="_blank"
              linkType="cta primary"
              dataTestid="go-to-expresseasy-cta"
            >
              {getCopy("linkText")}
              <IconContainer>
                <Icon
                  fillColor="var(--dui-color-white-500)"
                  icon={externalLinkIcon}
                  useAs="decorative"
                  width={{ mobile: "14px", desktop: "14px" }}
                  height={{ mobile: "14px", desktop: "14px" }}
                />
              </IconContainer>
            </Link>
          </StyledButtonContainer>
        </Card>
      </div>
    </div>
  );
};

DigitalServicesUnavailable.propTypes = {
  intl: PropTypes.any.isRequired,
};

export default injectIntl(DigitalServicesUnavailable);
